import { useEffect, useRef } from 'react';
import { apiGet, operationsClient } from '@/lib/api';
import { useContactPrefill } from '@/lib/contacts/contact-prefill-cache';
import type {
  EstimateContactAddress,
  EstimateContactLike,
} from '@/routes/_dashboard/dashboard/estimates/_shared';

export function toEstimateContactLike(contact: Record<string, unknown>): EstimateContactLike {
  return {
    id: contact.id != null ? String(contact.id) : undefined,
    _id: contact.id != null ? String(contact.id) : undefined,
    firstName: contact.firstName != null ? String(contact.firstName) : undefined,
    lastName: contact.lastName != null ? String(contact.lastName) : undefined,
    displayName: contact.displayName != null ? String(contact.displayName) : undefined,
    primaryEmail: contact.primaryEmail != null ? String(contact.primaryEmail) : undefined,
    primaryPhone: contact.primaryPhone != null ? String(contact.primaryPhone) : undefined,
    assignedTo: contact.assignedTo != null ? String(contact.assignedTo) : undefined,
    // Preserve the full address rows (incl. id + tax-exemption snapshot fields)
    // so document creation can copy them into serviceAddress.
    addresses: contact.addresses as EstimateContactAddress[] | undefined,
  };
}

interface UseEstimatePrefillContactArgs {
  prefillContactId?: string;
  isEditMode: boolean;
  onSelectContact: (contact: EstimateContactLike) => void;
  /**
   * NOT-579 — for the cold deep-link fetch path only: skip the late prefill if the
   * user already picked a customer while the request was in flight, so their
   * choice is never clobbered.
   */
  isCustomerAlreadySet?: () => boolean;
}

export function useEstimatePrefillContact({
  prefillContactId,
  isEditMode,
  onSelectContact,
  isCustomerAlreadySet,
}: UseEstimatePrefillContactArgs) {
  // NOT-579 — the contact page seeds the loaded record here, so arriving from a
  // contact hydrates synchronously with no network wait.
  const seededContact = useContactPrefill(prefillContactId);
  const didPrefillContactRef = useRef(false);
  // Read the LATEST guard at resolve time — the async .then closes over the
  // fetch-start render, so a plain closure would miss a customer the user picked
  // while the request was in flight. NOT-579 review.
  const isCustomerAlreadySetRef = useRef(isCustomerAlreadySet);
  isCustomerAlreadySetRef.current = isCustomerAlreadySet;

  useEffect(() => {
    if (isEditMode || didPrefillContactRef.current || !prefillContactId) return;

    // Instant path: the contact came over from the contact page.
    if (seededContact) {
      didPrefillContactRef.current = true;
      onSelectContact(toEstimateContactLike(seededContact as unknown as Record<string, unknown>));
      return;
    }

    // Cold deep-link: fetch, and never overwrite a customer the user chose while
    // the request was in flight.
    didPrefillContactRef.current = true;
    apiGet<Record<string, unknown>>(operationsClient, `/contacts/${prefillContactId}`)
      .then((contact) => {
        if (isCustomerAlreadySetRef.current?.()) return;
        onSelectContact(toEstimateContactLike(contact));
      })
      .catch(() => {});
  }, [seededContact, prefillContactId, isEditMode, onSelectContact]);
}
